/* eslint-disable indent */

import Vue from 'vue';
import { valueLogger } from 'utils';
import { DefineOwnModule, RootActions, RootMutations } from 'stores/type';
import defer from 'lodash/defer';
import BaseElementsManager from 'managers/base-elements.manager';
import ProgressManager from 'managers/progress.manager';
import EventsManager from 'managers/events.manager';
import { State, Getters, Actions, Mutations } from './types';

const moduleState: State = {
  progress: {},
  final: {},
  prime: [],
  base: [],
  total: [],
};

const storeModule: DefineOwnModule<
  State,
  Getters,
  Mutations & Pick<RootMutations, 'RESET_PROGRESS'>,
  Actions & Pick<RootActions, 'mixNew'>
> = {
  state: moduleState,

  getters: {
    progressObject: (state) => state.progress,
    progress: (state) => Object.keys(state.progress),
    prime: (state) => state.prime,
    finalObject: (state) => state.final,
    final: (state) => Object.keys(state.final),
    base: (state) => state.base,

    total: (state) => state.total,

    hasProgress: (_state, getters) => !!getters.progress.length,
    hasInProgress: (state) => (rawId) => {
      const id = `${ rawId }`;
      return state.progress.hasOwnProperty(id)
          || state.final.hasOwnProperty(id)
          || state.base.includes(id)
          || state.prime.includes(id);
    },
    hasUnlockedBase: (state) => (id) => state.base.includes(id),

    totalProgress: (_state, getters) => getters.totalProgressWithoutFinal + getters.final.length,
    totalProgressWithoutFinal: (_state, getters) => {
      let count = getters.progress.length;
      count += getters.prime.length;
      count += getters.base.length;
      return count;
    },
    totalProgressElements: (_state, getters) =>
      [...getters.progress, ...getters.prime, ...getters.base, ...getters.final],
    progressSince: (state) => (since) => state.total
      .filter(({ timestamp }) => timestamp > since)
      .map(({ id }) => id),

    elementTimestamp: (state) => (elementId) => {
      // backward search is faster in this case
      for (let i = state.total.length - 1; i >= 0; i--) {
        if (state.total[i].id === elementId) {
          return state.total[i].timestamp;
        }
      }

      return 0;
    },
    elementIsNewlyDiscovered: (_state, getters) => (elementId) => {
      const timestamp = getters.elementTimestamp(elementId);
      const parentsTimestamp = getters.historyLastTimestamp;
      return timestamp >= parentsTimestamp;
    },

    hasAllElements: (_state, getters) => getters.elementsCount > 0
      && getters.totalProgress >= getters.elementsCount,
    hasAllExhausted: (_state, getters) => getters.elementsCount > 0
      && getters.elementsIds.every((id) => {
        const element = getters.elements[id];
        if (element.final) return true;

        return !!getters.elementIsExhausted(id);
      }),
    hasEmptyLibrary: (_state, getters) => !getters.hasExhaustedVisible && getters.hasAllExhausted,
    hasAllCleared: (_state, getters) => getters.hasEmptyLibrary && !getters.workspaceHasInactive,

    isArtModeEnabled: (_state, getters) =>
      getters.hasAllCleared && !getters.needsGameCompletionAnimation,
  },


  actions: {
    // on base load or history reload
    progressCalculate({ commit, dispatch, getters }) {
      const data = ProgressManager.analyze(getters.elements, [...getters.history]);
      commit('PROGRESS_ANALYZED', data);

      const unlockedBaseElements = BaseElementsManager.getInitiallyUnlocked();
      if (unlockedBaseElements.length) {
        dispatch('progressNewBase', unlockedBaseElements);
      }

      dispatch('exhaustedCheckIds', getters.totalProgressElements);
      dispatch('libraryReload');
      defer(() => {
        EventsManager.emit('ENCYCLOPEDIA_PROGRESS_ANALYZED');
      });
    },

    mixNew({ commit, getters }, { parents, children }) {
      children.forEach((childId) => {
        if (getters.hasInLibrary(childId)) return;

        const element = getters.elementById(childId);
        if (!element) return;

        if (element.final) {
          commit('PROGRESS_FINAL', childId);
          return;
        }

        if (!getters.hasInProgress(childId)) {
          commit('PROGRESS_NEW', childId);
        }
      });

      // wait for 60ms for all computation to finish and then inform encyclopedia
      window.setTimeout(
        () => EventsManager.emit('ENCYCLOPEDIA_MIX_NEW', { parents, children, timestamp: Date.now() }),
        60,
      );
    },
    progressNewBase({ commit, getters }, ids) {
      const newBase = ids.filter((id) => !getters.base.includes(id));
      newBase.forEach((child) =>
        commit('PROGRESS_BASE', {
          id: child,
          timestamp: Date.now(),
        }),
      );

      EventsManager.emit('ENCYCLOPEDIA_MIX_BASE', newBase);
    },
  },


  mutations: {
    PROGRESS_ANALYZED(state, { progress, base, final, primes, timestamps }) {
      state.progress = progress;
      state.base = base;
      state.prime = primes;
      state.final = final;

      state.total = timestamps;

      valueLogger('progress', 'analyzed', state);
    },

    PROGRESS_NEW(state, id) {
      Vue.set(state.progress, id, true);
      state.total.push({ id, timestamp: Date.now() });
    },
    PROGRESS_FINAL(state, id) {
      if (state.final.hasOwnProperty(id)) return;

      Vue.set(state.final, id, true);
      state.total.push({ id, timestamp: Date.now() });
    },
    PROGRESS_BASE(state, { id, timestamp }) {
      state.base.push(id);
      state.total.push({ id, timestamp });
    },
    PROGRESS_PRIMES(state, primes) {
      state.prime = primes;
    },
    RESET_PROGRESS(state) {
      state.progress = {};
      state.final = {};
      state.base = [];
      state.total = [];
    },
  },
};

export default storeModule;
